import type {
  InboxAttentionState,
  InboxConversation,
  InboxPriority,
  InboxState,
  InboxSummary,
} from "../../types";

/** Tab order on the inbox page. */
export const INBOX_STATES: InboxState[] = [
  "needs_reply",
  "claimed",
  "resolved",
];

export function isInboxState(value: unknown): value is InboxState {
  return value === "needs_reply" || value === "claimed" || value === "resolved";
}

/** True while an episode still needs a human (claimed or not yet claimed). */
export function isAttentionOpen(state: string | null | undefined): boolean {
  return state === "needs_reply" || state === "claimed";
}

/** Contact display name: explicit name, then first/last, then "Contact". */
export function inboxContactName(
  row: Pick<InboxConversation, "contact_name" | "first_name" | "last_name">,
): string {
  const explicit = row.contact_name?.trim();
  if (explicit) return explicit;
  const composed = [row.first_name, row.last_name]
    .map((part) => part?.trim() ?? "")
    .filter(Boolean)
    .join(" ");
  return composed || "Contact";
}

export function initialsOf(name: string | null | undefined): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Regional-indicator flag for an ISO-3166 alpha-2 code ("ES" → 🇪🇸). */
export function flagEmoji(iso2: string | null | undefined): string {
  const code = (iso2 ?? "").trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return "";
  return String.fromCodePoint(
    ...[...code].map((char) => 0x1f1e6 + char.charCodeAt(0) - 65),
  );
}

const PRIORITY_RANK: Record<InboxPriority, number> = {
  high: 0,
  normal: 1,
  low: 2,
};

export function normalizePriority(
  value: string | null | undefined,
): InboxPriority {
  return value === "high" || value === "low" ? value : "normal";
}

export function priorityRank(value: string | null | undefined): number {
  return PRIORITY_RANK[normalizePriority(value)];
}

function parseTime(value: string | null | undefined): number {
  if (!value) return Number.NaN;
  return Date.parse(value);
}

/**
 * Stable triage order: high priority first, then the contact that has been
 * waiting the longest (`attention_since` ascending). Rows without a timestamp
 * sink to the end of their priority band. Pure — does not mutate the input.
 */
export function sortInboxRows<T extends InboxConversation>(rows: T[]): T[] {
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const rank =
        priorityRank(a.row.attention_priority) -
        priorityRank(b.row.attention_priority);
      if (rank !== 0) return rank;
      const ta = parseTime(a.row.attention_since);
      const tb = parseTime(b.row.attention_since);
      if (Number.isNaN(ta) && Number.isNaN(tb)) return a.index - b.index;
      if (Number.isNaN(ta)) return 1;
      if (Number.isNaN(tb)) return -1;
      if (ta !== tb) return ta - tb;
      return a.index - b.index;
    })
    .map((entry) => entry.row);
}

/**
 * Merge cursor pages into one list, de-duplicated by contact id (a contact
 * can slide between pages while the list is being polled). The first
 * occurrence wins so the newest page's state is kept.
 */
export function mergeInboxPages<T extends InboxConversation>(
  pages: Array<{ data: T[] } | undefined | null>,
): T[] {
  const seen = new Set<string>();
  const merged: T[] = [];
  for (const page of pages) {
    for (const row of page?.data ?? []) {
      const key = String(row.contact_id ?? row.id);
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(row);
    }
  }
  return merged;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Compact "how long ago": "just now", "5m", "3h", "2d", "3w". Empty for
 *  invalid input. Future timestamps clamp to "just now". */
export function formatRelativeTime(
  value: string | null | undefined,
  now: number | Date = Date.now(),
): string {
  const time = parseTime(value);
  if (Number.isNaN(time)) return "";
  const nowMs = typeof now === "number" ? now : now.getTime();
  const elapsed = Math.max(0, nowMs - time);
  if (elapsed < MINUTE) return "just now";
  if (elapsed < HOUR) return `${Math.floor(elapsed / MINUTE)}m`;
  if (elapsed < DAY) return `${Math.floor(elapsed / HOUR)}h`;
  if (elapsed < 7 * DAY) return `${Math.floor(elapsed / DAY)}d`;
  return `${Math.floor(elapsed / (7 * DAY))}w`;
}

/** "23h 12m" / "45m" / "<1m" for a positive duration. */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < MINUTE) return "<1m";
  const hours = Math.floor(ms / HOUR);
  const minutes = Math.floor((ms % HOUR) / MINUTE);
  if (hours <= 0) return `${minutes}m`;
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
  }
  return `${hours}h ${minutes}m`;
}

export type FreeformWindowStatus =
  | { kind: "open"; remainingMs: number; label: string }
  | { kind: "closed" }
  | { kind: "unknown" };

/** Where the 24h freeform window stands relative to `now`. `unknown` when the
 *  backend gave no expiry (e.g. no inbound message ever). */
export function freeformWindowStatus(
  expiresAt: string | null | undefined,
  now: number | Date = Date.now(),
): FreeformWindowStatus {
  const expires = parseTime(expiresAt);
  if (Number.isNaN(expires)) return { kind: "unknown" };
  const nowMs = typeof now === "number" ? now : now.getTime();
  const remainingMs = expires - nowMs;
  if (remainingMs <= 0) return { kind: "closed" };
  return { kind: "open", remainingMs, label: formatDuration(remainingMs) };
}

/** Count shown on a state tab / the nav badge; `null` when unknown. */
export function summaryCount(
  summary: InboxSummary | null | undefined,
  state: InboxState,
): number | null {
  const value = summary?.[state];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

const ATTENTION_TONE_CLASS: Record<InboxAttentionState, string> = {
  needs_reply:
    "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200",
  claimed:
    "border-sky-300 bg-sky-50 text-sky-900 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-200",
  resolved:
    "border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200",
  none: "",
};

/** Tailwind classes for a banner/badge tinted by attention state. */
export function attentionToneClass(state: InboxAttentionState): string {
  return ATTENTION_TONE_CLASS[state];
}

/** Human label for an attention reason: backend label first, then a readable
 *  fallback derived from the outcome key ("fallback_unknown_command" →
 *  "Fallback unknown command"). */
export function attentionReasonLabel(
  row: Pick<InboxConversation, "attention_reason" | "attention_reason_label">,
): string | null {
  const label = row.attention_reason_label?.trim();
  if (label) return label;
  const key = row.attention_reason?.trim();
  if (!key) return null;
  const words = key.replace(/[_-]+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Inbox reason of a stalled my.echoflicks.com creation (MYC-015). */
export const WEB_CREATE_STALL_REASON = "web_create_stalled";

export type WebCreateStall = {
  /** Which part stopped moving; null when the bot did not say. */
  stage: "felix" | "render" | null;
  /** The Echo page (Echo id = workload id), when the episode names it. */
  echoPath: string | null;
};

/**
 * A web Create stall episode: the customer waits on my.echoflicks.com, not in
 * WhatsApp, so the operator wants the Echo (and its Web transcript) rather than
 * the chat. Null for every WhatsApp episode.
 */
export function webCreateStall(
  row: Pick<
    InboxConversation,
    | "attention_reason"
    | "attention_message_type"
    | "attention_web_stage"
    | "attention_workload_id"
  >,
): WebCreateStall | null {
  if (
    row.attention_reason !== WEB_CREATE_STALL_REASON &&
    row.attention_message_type !== "web_create"
  ) {
    return null;
  }
  const stage =
    row.attention_web_stage === "felix" || row.attention_web_stage === "render"
      ? row.attention_web_stage
      : null;
  const workloadId = row.attention_workload_id?.trim();
  return {
    stage,
    echoPath: workloadId
      ? `/echoflicks_video_sessions/${encodeURIComponent(workloadId)}/show`
      : null,
  };
}
